// Define the enums for the different status types
import { DisputeStatusEnum } from "@common/types/DisputeTypes.ts";
import { BillingStatusEnum } from "@common/enums/BillingStatusEnum.ts";

// Re-exported so existing imports from this module keep working while the enum
// itself is shared with the backend.
export { BillingStatusEnum };

export enum MainStatusEnum {
    NEW = 'all-new',
    PENDING = 'all-pending',
    SENT = 'all-sent',
    TRASH = 'all-trash',
    ALL = 'all'
}

// The bare main-status values stored in the status hierarchy (the `all-` prefix
// of MainStatusEnum stripped). Use these instead of `status.replace('all-', '')`.
export enum BaseStatusEnum {
    NEW = 'new',
    PENDING = 'pending',
    SENT = 'sent',
    TRASH = 'trash',
    ALL = 'all'
}

export enum NewStatusEnum {
    ALL = 'all-new',
    ASSIGNED = 'assigned-new',
    NOT_ASSIGNED = 'not-assigned-new',
    NO_BUYERS = 'no-buyers-new'
}

export enum PendingStatusEnum {
    ALL = 'all-pending',
    ASSIGNED = 'assigned-pending',
    CONFIRMED = 'confirmed-pending',
    PENDING_CHARGE = 'pending-charge-pending'
}

export enum SentStatusEnum {
    ALL = 'all-sent',
    DISPUTED = 'disputed-sent',
    BILLING = 'billing-sent'
}

export enum TrashStatusEnum {
    ALL = 'all-trash',
    NOT_ASSIGNED = 'not-assigned-trash',
    NO_BUYERS = 'no-buyers-trash'
}

export enum AllStatusEnum {
    ALL = 'all',
    EXCLUDE_TRASH = 'all-exclude-trash'
}

// Maps each (prefixed) main status to its bare base value.
export const MainToBaseStatusMap: Record<MainStatusEnum, BaseStatusEnum> = {
    [MainStatusEnum.NEW]: BaseStatusEnum.NEW,
    [MainStatusEnum.PENDING]: BaseStatusEnum.PENDING,
    [MainStatusEnum.SENT]: BaseStatusEnum.SENT,
    [MainStatusEnum.TRASH]: BaseStatusEnum.TRASH,
    [MainStatusEnum.ALL]: BaseStatusEnum.ALL
};

export const DefaultMainStatusMap: Record<MainStatusEnum, string> = {
    [MainStatusEnum.NEW]: NewStatusEnum.ALL,
    [MainStatusEnum.PENDING]: PendingStatusEnum.ALL,
    [MainStatusEnum.SENT]: SentStatusEnum.ALL,
    [MainStatusEnum.TRASH]: TrashStatusEnum.ALL,
    [MainStatusEnum.ALL]: AllStatusEnum.ALL
};

// Default state (secondary level) for a bare base main status.
export const DefaultBaseStatusMap: Record<BaseStatusEnum, string> = {
    [BaseStatusEnum.NEW]: NewStatusEnum.ALL,
    [BaseStatusEnum.PENDING]: PendingStatusEnum.ALL,
    [BaseStatusEnum.SENT]: SentStatusEnum.ALL,
    [BaseStatusEnum.TRASH]: TrashStatusEnum.ALL,
    [BaseStatusEnum.ALL]: AllStatusEnum.ALL
};

// Define the interfaces for the option structures
export interface StatusOption {
    status: string;
    label: string;
    chip: string;
    options?: StatusOption[];
}

// Create the typed leadStatusOptions array
export const leadStatusOptions: StatusOption[] = [
    {
        status: MainStatusEnum.NEW,
        label: 'New',
        chip: 'New',
        options: [
            { status: NewStatusEnum.ALL, label: 'All New', chip: 'New' },
            { status: NewStatusEnum.ASSIGNED, label: 'Assigned', chip: 'New: Assigned' },
            { status: NewStatusEnum.NOT_ASSIGNED, label: 'Not Assigned', chip: 'New: Unassigned' },
            { status: NewStatusEnum.NO_BUYERS, label: 'No Buyers', chip: 'New: No Buyers' },
        ]
    },
    {
        status: MainStatusEnum.PENDING,
        label: 'Pending',
        chip: 'Pending',
        options: [
            { status: PendingStatusEnum.ALL, label: 'All Pending', chip: 'Pending' },
            { status: PendingStatusEnum.ASSIGNED, label: 'Assigned', chip: 'Pending: Assigned' },
            { status: PendingStatusEnum.CONFIRMED, label: 'Confirmed', chip: 'Pending: Confirmed' },
            { status: PendingStatusEnum.PENDING_CHARGE, label: 'Pending Charge', chip: 'Pending: Awaiting Payment' },
        ]
    },
    {
        status: MainStatusEnum.SENT,
        label: 'Sent',
        chip: 'Sent',
        options: [
            { status: SentStatusEnum.ALL, label: 'All Sent', chip: 'Sent' },
            {
                status: SentStatusEnum.DISPUTED,
                label: 'Disputed',
                chip: 'Sent: Disputed',
                options: [
                    { status: DisputeStatusEnum.PENDING, label: 'Pending', chip: 'Sent: Dispute Pending Review' },
                    { status: DisputeStatusEnum.APPROVED, label: 'Approved', chip: 'Sent: Dispute Approved' },
                    { status: DisputeStatusEnum.REJECTED, label: 'Rejected', chip: 'Sent: Dispute Rejected' },
                ]
            },
            {
                status: SentStatusEnum.BILLING,
                label: 'Billing',
                chip: 'Sent: Transaction Pending',
                options: [
                    { status: BillingStatusEnum.PENDING, label: 'Pending', chip: 'Sent: Transaction Pending' },
                    { status: BillingStatusEnum.CHARGED, label: 'Charged', chip: 'Sent: Transaction Charged' },
                    { status: BillingStatusEnum.REFUNDED, label: 'Refunded', chip: 'Sent: Transaction Refunded' },
                ]
            },
        ]
    },
    {
        status: MainStatusEnum.TRASH,
        label: 'Trash',
        chip: 'Trash',
        options: [
            { status: TrashStatusEnum.ALL, label: 'All Trashed', chip: 'Trash' },
            { status: TrashStatusEnum.NOT_ASSIGNED, label: 'Not Assigned', chip: 'Trash: Unassigned' },
            { status: TrashStatusEnum.NO_BUYERS, label: 'No Buyers', chip: 'Trash: No Buyers' },
        ]
    },
    {
        status: MainStatusEnum.ALL,
        label: 'All',
        chip: 'All',
        options: [
            { status: AllStatusEnum.ALL, label: 'Include Trash', chip: 'Include Trash' },
            { status: AllStatusEnum.EXCLUDE_TRASH, label: 'Exclude Trash', chip: 'All (Untrashed Only)' },
        ]
    },
];

// Helper functions to work with the typed status options

/**
 * Gets a status option by its status value
 */
export function getStatusOptionByValue(statusValue: string): StatusOption | null {
    // Check in main options
    for (const mainOption of leadStatusOptions) {
        if (mainOption.status === statusValue) {
            return mainOption;
        }

        // Check in first level options
        if (mainOption.options != null) {
            for (const option of mainOption.options) {
                if (option.status === statusValue) {
                    return option;
                }

                // Check in nested options if available
                if (option.options != null) {
                    const nestedOption = option.options.find(o => o.status === statusValue);
                    if (nestedOption != null) {
                        return nestedOption;
                    }
                }
            }
        }
    }

    return null;
}

/**
 * Gets the main status option that contains a specific status value
 */
export function getMainStatusForValue(statusValue: string): StatusOption | null {
    for (const mainOption of leadStatusOptions) {
        if (mainOption.status === statusValue) {
            return mainOption;
        }

        // Check if this status is in any of the options
        if (mainOption.options != null) {
            const foundInOptions = mainOption.options.some(option => {
                if (option.status === statusValue) {
                    return true;
                }

                // Check nested options
                if (option.options != null) {
                    return option.options.some(o => o.status === statusValue);
                }

                return false;
            });

            if (foundInOptions) {
                return mainOption;
            }
        }
    }

    return null;
}

/**
 * Ged default option for selected main status
 */
export function getDefaultStatusForMain(status: MainStatusEnum): string {
    return DefaultMainStatusMap[status];
}

/**
 * Strips the `all-` prefix off a main status value, returning the bare base
 * status stored in the hierarchy (e.g. 'all-new' -> 'new', 'all' -> 'all').
 * Replaces the scattered `status.replace('all-', '')` idiom.
 */
export function getBaseStatus(status: string): string {
    return MainToBaseStatusMap[status as MainStatusEnum] ?? status;
}

/**
 * Gets the default state (secondary level) for a bare base main status.
 */
export function getDefaultStateForBaseStatus(baseStatus: string): string {
    return DefaultBaseStatusMap[baseStatus as BaseStatusEnum] ?? NewStatusEnum.ALL;
}

export const verifiedOptions = [
    { label: 'Verified', status: 'verified', chip: 'Verified' },
    { label: 'Unverified', status: 'unverified', chip: 'Unverified' },
];
